import React, { createContext, useContext } from 'react'

import { useScriptStateInternal } from '@/components/Event/NewScript/hooks/state/useScriptState'
import { DRScriptData, Event } from '@/types'

export type ScriptContextValue = Omit<
  ReturnType<typeof useScriptStateInternal>,
  'state'
> & { state: DRScriptData }

const ScriptContext = createContext<ScriptContextValue | null>(null)

type ScriptProviderProps = {
  event: Event
  loading: React.ReactNode
  error: React.ReactNode
  children: React.ReactNode
}

/**
 * Owns the single script-state subscription for one event.
 *
 * Children mount only once script state has loaded. Every consumer reads this
 * one instance, so there is exactly one React Query subscription, one action
 * set, and one debounced upload timer per script.
 */
const EventScriptProvider = ({
  event,
  loading,
  error,
  children,
}: ScriptProviderProps) => {
  const value = useScriptStateInternal(event)

  if (value.isLoading) {
    return <>{loading}</>
  }

  if (value.isError || !value.state) {
    return <>{error}</>
  }

  return (
    <ScriptContext.Provider value={{ ...value, state: value.state }}>
      {children}
    </ScriptContext.Provider>
  )
}

export const ScriptProvider = (props: ScriptProviderProps) => (
  <EventScriptProvider key={props.event.id} {...props} />
)

export const useScriptState = (): ScriptContextValue => {
  const value = useContext(ScriptContext)

  if (value === null) {
    throw new Error('useScriptState must be used inside a ScriptProvider')
  }

  return value
}
